// src/scenes/meal-entry/MealCountsDashboard.jsx

import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    useTheme,
    Modal,
    IconButton,
    Switch,
    FormControlLabel,
    CircularProgress,
    Typography,
    Paper,
    Divider,
    Chip,
    Stack,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import FreeBreakfastIcon from "@mui/icons-material/FreeBreakfast";
import LunchDiningIcon from "@mui/icons-material/LunchDining";
import LocalDiningIcon from "@mui/icons-material/LocalDining";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import PeopleIcon from "@mui/icons-material/People";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import FilterListIcon from "@mui/icons-material/FilterList";
import { DataGridPro } from "@mui/x-data-grid-pro";
import {
    ResponsiveContainer,
    AreaChart,
    Area,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartTooltip,
    Legend,
} from "recharts";
import { useNavigate } from "react-router-dom";
import Header from "../../components/Header";
import { tokens } from "../../theme";

const API_MEAL_COUNTS = "https://figazuresql.swancreate.com/api/swa/FINANCE_MealCounts";
const API_SERVICE_DAYS = "https://figazuresql.swancreate.com/api/swa/vw_ServiceDaysByMonth";
const API_REIMBURSEMENT_RATES = "https://figazuresql.swancreate.com/api/swa/FINANCE_MealRates";

const MONTH_ABBR = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const abbrToIdx = (abbr) => MONTH_ABBR.indexOf(abbr);

/** ✅ MOVED OUTSIDE COMPONENT (prevents remount "refresh" loop) */
function StatCard({ title, value, icon, color, reimbursement, colors }) {
    return (
        <Paper
            elevation={8}
            sx={{
                position: "relative",
                overflow: "hidden",
                background: `linear-gradient(145deg, ${color}18 0%, ${color}08 100%)`,
                border: `3px solid ${color}`,
                borderRadius: 4,
                p: 3,
                minHeight: 180,
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                cursor: "pointer",
                "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: `radial-gradient(circle at 30% 50%, ${color}25 0%, transparent 60%)`,
                    opacity: 0,
                    transition: "opacity 0.4s ease",
                },
                "&:hover": {
                    transform: "translateY(-12px) scale(1.02)",
                    boxShadow: `0 20px 60px ${color}55, 0 0 80px ${color}40, inset 0 0 40px ${color}15`,
                    border: `3px solid ${color}`,
                    "&::before": {
                        opacity: 1,
                    },
                },
                boxShadow: `0 10px 40px ${color}35, 0 0 30px ${color}25`,
            }}
        >
            <Box position="relative" zIndex={1}>
                <Box
                    sx={{
                        display: "inline-flex",
                        p: 1.5,
                        borderRadius: "50%",
                        background: `linear-gradient(135deg, ${color}40 0%, ${color}20 100%)`,
                        mb: 2,
                        boxShadow: `0 8px 24px ${color}40, inset 0 0 20px ${color}30`,
                    }}
                >
                    {icon}
                </Box>

                <Typography
                    variant="subtitle2"
                    sx={{
                        color: colors.grey[300],
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: 2,
                        fontSize: "0.75rem",
                        mb: 1,
                    }}
                >
                    {title}
                </Typography>

                <Typography
                    variant="caption"
                    sx={{
                        color: colors.grey[400],
                        fontWeight: 600,
                        fontSize: "0.7rem",
                        display: "block",
                        mb: 0.5,
                    }}
                >
                    Average Daily:
                </Typography>

                <Typography
                    variant="h1"
                    sx={{
                        color: color,
                        fontWeight: 900,
                        fontSize: "3.5rem",
                        lineHeight: 1,
                        textShadow: `0 0 40px ${color}88, 0 0 20px ${color}66, 0 4px 12px rgba(0,0,0,0.5)`,
                        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                        mb: 2,
                    }}
                >
                    {Number(value || 0).toLocaleString()}
                </Typography>

                {reimbursement !== undefined && (
                    <>
                        <Divider sx={{ my: 1.5, borderColor: `${color}40` }} />
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                background: `linear-gradient(135deg, ${color}12 0%, ${color}05 100%)`,
                                borderRadius: 2,
                                p: 1.5,
                                border: `1px solid ${color}30`,
                            }}
                        >
                            <Box display="flex" alignItems="center" gap={1}>
                                <AttachMoneyIcon sx={{ color: "#4CAF50", fontSize: 24 }} />
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: colors.grey[400],
                                        fontWeight: 600,
                                        textTransform: "uppercase",
                                        letterSpacing: 1,
                                        fontSize: "0.65rem",
                                    }}
                                >
                                    Average Daily Reimbursement
                                </Typography>
                            </Box>
                            <Typography
                                variant="h4"
                                sx={{
                                    color: "#4CAF50",
                                    fontWeight: 800,
                                    textShadow: "0 0 15px #4CAF5066",
                                }}
                            >
                                $
                                {Number(reimbursement || 0).toLocaleString("en-US", {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                })}
                            </Typography>
                        </Box>
                    </>
                )}
            </Box>
        </Paper>
    );
}

/** ✅ MOVED OUTSIDE COMPONENT (prevents remount "refresh" loop) */
function ChartContainer({
    title,
    subtitle,
    icon,
    accentColor,
    children,
    chartType,
    hoveredChart,
    setHoveredChart,
    colors,
}) {
    return (
        <Paper
            elevation={hoveredChart === chartType ? 24 : 12}
            onMouseEnter={() => setHoveredChart(chartType)}
            onMouseLeave={() => setHoveredChart(null)}
            sx={{
                position: "relative",
                overflow: "hidden",
                backgroundColor: "transparent",
                background: `linear-gradient(145deg, ${colors.primary[400]}cc 0%, ${colors.primary[500]}cc 100%)`,
                borderRadius: 3,
                p: 3,
                mb: 3,
                transition: "all 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
                border: `2px solid ${hoveredChart === chartType ? accentColor : colors.primary[300]}`,
                backdropFilter: "blur(10px)",
                "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    height: "6px",
                    background: `linear-gradient(90deg, ${accentColor} 0%, ${accentColor}88 100%)`,
                    boxShadow: `0 0 20px ${accentColor}`,
                },
                "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: `0 20px 60px ${accentColor}40, 0 0 40px ${accentColor}30`,
                },
            }}
        >
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
                <Box display="flex" alignItems="center" gap={2}>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 56,
                            height: 56,
                            borderRadius: "16px",
                            background: `linear-gradient(135deg, ${accentColor}30 0%, ${accentColor}10 100%)`,
                            border: `2px solid ${accentColor}60`,
                            boxShadow: `0 8px 24px ${accentColor}40, inset 0 0 20px ${accentColor}20`,
                            transition: "all 0.3s ease",
                            ...(hoveredChart === chartType && {
                                transform: "scale(1.1) rotate(5deg)",
                                boxShadow: `0 12px 32px ${accentColor}60, inset 0 0 30px ${accentColor}30`,
                            }),
                        }}
                    >
                        {icon}
                    </Box>
                    <Box>
                        <Typography
                            variant="h4"
                            sx={{
                                color: colors.grey[100],
                                fontWeight: 800,
                                textTransform: "uppercase",
                                letterSpacing: 1.5,
                                textShadow: `0 2px 8px ${accentColor}40`,
                            }}
                        >
                            {title}
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                color: colors.grey[400],
                                fontWeight: 600,
                                mt: 0.5,
                            }}
                        >
                            {subtitle}
                        </Typography>
                    </Box>
                </Box>
                <Stack direction="row" spacing={1}>
                    <Chip
                        icon={<TrendingUpIcon sx={{ fontSize: 18 }} />}
                        label={chartType === "counts" ? "Meal Counts" : "Reimbursement"}
                        sx={{
                            backgroundColor: `${accentColor}20`,
                            color: accentColor,
                            fontWeight: 700,
                            border: `2px solid ${accentColor}40`,
                            boxShadow: `0 4px 12px ${accentColor}30`,
                            "& .MuiChip-icon": {
                                color: accentColor,
                            },
                        }}
                    />
                </Stack>
            </Stack>
            <Box
                sx={{
                    height: chartType === "counts" ? 340 : 320,
                    transition: "height 0.3s ease",
                }}
            >
                {children}
            </Box>
        </Paper>
    );
}

export default function MealCountsDashboard() {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const navigate = useNavigate();

    const [data, setData] = useState([]);
    const [serviceDaysData, setServiceDaysData] = useState([]);
    const [reimbursementRates, setReimbursementRates] = useState([]);
    const [siteFilter, setSiteFilter] = useState("");
    const [monthFilter, setMonthFilter] = useState("");
    const [showForm, setShowForm] = useState(false);
    const [comparePrev, setComparePrev] = useState(false);
    const [loading, setLoading] = useState(true);
    const [overOnly, setOverOnly] = useState(false);

    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(20);
    const [hoveredChart, setHoveredChart] = useState(null);

    const today = new Date();
    const defaultSYStartYear = today.getMonth() >= 6 ? today.getFullYear() : today.getFullYear() - 1;
    const [selectedSYStartYear, setSelectedSYStartYear] = useState(defaultSYStartYear);

    // ✅ CRITICAL: stable boundaries (stops "refresh" re-memo loops)
    const { syStart, syEnd, prevStart, prevEnd } = useMemo(() => {
        return {
            syStart: new Date(selectedSYStartYear, 6, 1),
            syEnd: new Date(selectedSYStartYear + 1, 5, 30),
            prevStart: new Date(selectedSYStartYear - 1, 6, 1),
            prevEnd: new Date(selectedSYStartYear, 5, 30),
        };
    }, [selectedSYStartYear]);

    useEffect(() => {
        setLoading(true);
        Promise.all([
            fetch(API_MEAL_COUNTS).then((r) => r.json()),
            fetch(API_SERVICE_DAYS).then((r) => r.json()),
            fetch(API_REIMBURSEMENT_RATES).then((r) => r.json()),
        ])
            .then(([mealCounts, serviceDays, rates]) => {
                setData(mealCounts);
                setServiceDaysData(serviceDays);
                setReimbursementRates(rates);
                setLoading(false);
            })
            .catch((err) => {
                console.error(err);
                setLoading(false);
            });
    }, []);

    const schoolYears = useMemo(() => {
        const years = new Set();
        data.forEach((r) => {
            const d = new Date(r.Date);
            if (isNaN(d)) return;
            const sy = d.getMonth() >= 6 ? d.getFullYear() : d.getFullYear() - 1;
            years.add(sy);
        });
        return Array.from(years)
            .sort((a, b) => b - a)
            .map((y) => ({ value: y, label: `${y}-${y + 1}` }));
    }, [data]);

    const sites = useMemo(() => Array.from(new Set(data.map((r) => r.SchoolName).filter(Boolean))), [data]);

    const months = useMemo(() => {
        const setM = new Set();
        data.forEach((r) => {
            const d = new Date(r.Date);
            if (isNaN(d)) return;
            if (d < syStart || d > syEnd) return;
            setM.add(`${d.toLocaleString("en-US", { month: "short" })} ${d.getFullYear()}`);
        });

        const arr = Array.from(setM);
        return arr.sort((a, b) => {
            const [ma, ya] = a.split(" ");
            const [mb, yb] = b.split(" ");
            if (ya !== yb) return parseInt(ya, 10) - parseInt(yb, 10);
            const iA = (abbrToIdx(ma) + 6) % 12;
            const iB = (abbrToIdx(mb) + 6) % 12;
            return iA - iB;
        });
    }, [data, syStart, syEnd]);

    const num = useCallback((v) => (v == null || v === "" ? 0 : Number(v)), []);

    const getPart = useCallback(
        (row, prefix) => {
            const candidates = [`${prefix}_participation`, `${prefix}_Participation`, `${prefix}_pct`, `${prefix}_Pct`];
            for (const k of candidates) {
                if (Object.prototype.hasOwnProperty.call(row, k)) return num(row[k]);
            }
            return 0;
        },
        [num]
    );

    const isOver = useCallback(
        (row) => getPart(row, "BR") > 100 || getPart(row, "LN") > 100 || getPart(row, "SN") > 100 || getPart(row, "SP") > 100,
        [getPart]
    );

    const sumInto = (bucket, row) => {
        bucket.Breakfast += (+row.BR_Free || 0) + (+row.BR_Paid || 0) + (+row.BR_Reduced || 0);
        bucket.Lunch += (+row.LN_Free || 0) + (+row.LN_Paid || 0) + (+row.LN_Reduced || 0);
        bucket.Snack += (+row.SN_Free || 0);
        bucket.Supper += (+row.SP_Free || 0) + (+row.SP_Paid || 0) + (+row.SP_Reduced || 0);
    };

    const parseMonthFilter = useCallback((mf) => {
        if (!mf) return null;
        const parts = mf.replace(/\s+/g, " ").trim().split(" ");
        if (parts.length !== 2) return null;
        const [abbr, yearStr] = parts;
        const monthIdx = abbrToIdx(abbr);
        const year = parseInt(yearStr, 10);
        if (monthIdx < 0 || Number.isNaN(year)) return null;
        return { monthIdx, year, monAbbr: abbr };
    }, []);

    const filtered = useMemo(
        () =>
            data.filter((r) => {
                const d = new Date(r.Date);
                if (isNaN(d) || d < syStart || d > syEnd) return false;
                if (siteFilter && r.SchoolName !== siteFilter) return false;
                if (monthFilter) {
                    const lbl = `${d.toLocaleString("en-US", { month: "short" })} ${d.getFullYear()}`;
                    if (lbl !== monthFilter) return false;
                }
                if (overOnly && !isOver(r)) return false;
                return true;
            }),
        [data, syStart, syEnd, siteFilter, monthFilter, overOnly, isOver]
    );

    const getRate = useCallback(
        (mealService, paymentStatus) => {
            const rate = reimbursementRates.find(
                (r) =>
                    r.MealType?.toLowerCase() === mealService.toLowerCase() &&
                    r.MealStatusType?.toLowerCase() === paymentStatus.toLowerCase()
            );
            return rate ? parseFloat(rate.TotalRate || rate.FederalRate || 0) : 0;
        },
        [reimbursementRates]
    );

    const statsData = useMemo(() => {
        let totalServiceDays = 0;
        let totals = { Breakfast: 0, Lunch: 0, Snack: 0, Supper: 0 };
        let reimbursements = { Breakfast: 0, Lunch: 0, Snack: 0, Supper: 0 };

        if (monthFilter) {
            const parsed = parseMonthFilter(monthFilter);
            if (parsed) {
                const { monthIdx, year } = parsed;
                const yearMonth = `${year}-${String(monthIdx + 1).padStart(2, "0")}`;

                const sdRecord = serviceDaysData.find((sd) => sd.YearMonth === yearMonth);
                totalServiceDays = sdRecord ? sdRecord.ServiceDays : 0;

                data.forEach((r) => {
                    const d = new Date(r.Date);
                    if (isNaN(d)) return;
                    if (d.getFullYear() !== year || d.getMonth() !== monthIdx) return;
                    if (siteFilter && r.SchoolName !== siteFilter) return;
                    if (overOnly && !isOver(r)) return;

                    sumInto(totals, r);

                    reimbursements.Breakfast +=
                        (+r.BR_Free || 0) * getRate("Breakfast", "Free") +
                        (+r.BR_Paid || 0) * getRate("Breakfast", "Paid") +
                        (+r.BR_Reduced || 0) * getRate("Breakfast", "Reduced");
                    reimbursements.Lunch +=
                        (+r.LN_Free || 0) * getRate("Lunch", "Free") +
                        (+r.LN_Paid || 0) * getRate("Lunch", "Paid") +
                        (+r.LN_Reduced || 0) * getRate("Lunch", "Reduced");
                    reimbursements.Snack += (+r.SN_Free || 0) * getRate("Snack", "Free");
                    reimbursements.Supper +=
                        (+r.SP_Free || 0) * getRate("Supper", "Free") +
                        (+r.SP_Paid || 0) * getRate("Supper", "Paid") +
                        (+r.SP_Reduced || 0) * getRate("Supper", "Reduced");
                });
            }
        } else {
            const uniqueDates = new Set();
            data.forEach((r) => {
                const d = new Date(r.Date);
                if (isNaN(d) || d < syStart || d > syEnd) return;
                if (siteFilter && r.SchoolName !== siteFilter) return;
                if (overOnly && !isOver(r)) return;

                const hasService =
                    (+r.BR_Free || 0) +
                    (+r.BR_Paid || 0) +
                    (+r.BR_Reduced || 0) +
                    (+r.LN_Free || 0) +
                    (+r.LN_Paid || 0) +
                    (+r.LN_Reduced || 0) +
                    (+r.SN_Free || 0) +
                    (+r.SP_Free || 0) +
                    (+r.SP_Paid || 0) +
                    (+r.SP_Reduced || 0) >
                    0;

                if (hasService) uniqueDates.add(d.toISOString().split("T")[0]);

                sumInto(totals, r);

                reimbursements.Breakfast +=
                    (+r.BR_Free || 0) * getRate("Breakfast", "Free") +
                    (+r.BR_Paid || 0) * getRate("Breakfast", "Paid") +
                    (+r.BR_Reduced || 0) * getRate("Breakfast", "Reduced");
                reimbursements.Lunch +=
                    (+r.LN_Free || 0) * getRate("Lunch", "Free") +
                    (+r.LN_Paid || 0) * getRate("Lunch", "Paid") +
                    (+r.LN_Reduced || 0) * getRate("Lunch", "Reduced");
                reimbursements.Snack += (+r.SN_Free || 0) * getRate("Snack", "Free");
                reimbursements.Supper +=
                    (+r.SP_Free || 0) * getRate("Supper", "Free") +
                    (+r.SP_Paid || 0) * getRate("Supper", "Paid") +
                    (+r.SP_Reduced || 0) * getRate("Supper", "Reduced");
            });
            totalServiceDays = uniqueDates.size;
        }

        return {
            serviceDays: totalServiceDays,
            avgBreakfast: totalServiceDays > 0 ? Math.round(totals.Breakfast / totalServiceDays) : 0,
            avgLunch: totalServiceDays > 0 ? Math.round(totals.Lunch / totalServiceDays) : 0,
            avgSnack: totalServiceDays > 0 ? Math.round(totals.Snack / totalServiceDays) : 0,
            avgSupper: totalServiceDays > 0 ? Math.round(totals.Supper / totalServiceDays) : 0,
            avgReimbBreakfast: totalServiceDays > 0 ? reimbursements.Breakfast / totalServiceDays : 0,
            avgReimbLunch: totalServiceDays > 0 ? reimbursements.Lunch / totalServiceDays : 0,
            avgReimbSnack: totalServiceDays > 0 ? reimbursements.Snack / totalServiceDays : 0,
            avgReimbSupper: totalServiceDays > 0 ? reimbursements.Supper / totalServiceDays : 0,
        };
    }, [data, serviceDaysData, monthFilter, siteFilter, overOnly, isOver, syStart, syEnd, parseMonthFilter, getRate]);

    const { currentAggMonthly, prevAggMonthly } = useMemo(() => {
        const curr = {}, prev = {};
        data.forEach((r) => {
            const d = new Date(r.Date);
            if (isNaN(d)) return;
            if (siteFilter && r.SchoolName !== siteFilter) return;
            if (overOnly && !isOver(r)) return;

            const m = d.toLocaleString("en-US", { month: "short" });
            const y = d.getFullYear();
            const key = `${m} ${y}`;

            if (d >= syStart && d <= syEnd) {
                curr[key] = curr[key] || { Breakfast: 0, Lunch: 0, Snack: 0, Supper: 0 };
                sumInto(curr[key], r);
            }
            if (d >= prevStart && d <= prevEnd) {
                prev[key] = prev[key] || { Breakfast: 0, Lunch: 0, Snack: 0, Supper: 0 };
                sumInto(prev[key], r);
            }
        });
        return { currentAggMonthly: curr, prevAggMonthly: prev };
    }, [data, siteFilter, overOnly, isOver, syStart, syEnd, prevStart, prevEnd]);

    const monthOrderWithYears = useMemo(() => {
        const result = [];
        const startMonth = 6;
        for (let i = 0; i < 12; i++) {
            const monthIdx = (startMonth + i) % 12;
            const year = monthIdx < 6 ? selectedSYStartYear + 1 : selectedSYStartYear;
            const monthAbbr = new Date(0, monthIdx).toLocaleString("en-US", { month: "short" });
            result.push(`${monthAbbr} ${year}`);
        }
        return result;
    }, [selectedSYStartYear]);

    const { reimbAggMonthly, reimbAggMonthlyPrev } = useMemo(() => {
        const curr = {}, prev = {};

        data.forEach((r) => {
            const d = new Date(r.Date);
            if (isNaN(d)) return;
            if (siteFilter && r.SchoolName !== siteFilter) return;
            if (overOnly && !isOver(r)) return;

            const m = d.toLocaleString("en-US", { month: "short" });
            const y = d.getFullYear();
            const key = `${m} ${y}`;

            if (d >= syStart && d <= syEnd) {
                curr[key] = curr[key] || { Breakfast: 0, Lunch: 0, Snack: 0, Supper: 0 };
                curr[key].Breakfast +=
                    (+r.BR_Free || 0) * getRate("Breakfast", "Free") +
                    (+r.BR_Paid || 0) * getRate("Breakfast", "Paid") +
                    (+r.BR_Reduced || 0) * getRate("Breakfast", "Reduced");
                curr[key].Lunch +=
                    (+r.LN_Free || 0) * getRate("Lunch", "Free") +
                    (+r.LN_Paid || 0) * getRate("Lunch", "Paid") +
                    (+r.LN_Reduced || 0) * getRate("Lunch", "Reduced");
                curr[key].Snack += (+r.SN_Free || 0) * getRate("Snack", "Free");
                curr[key].Supper +=
                    (+r.SP_Free || 0) * getRate("Supper", "Free") +
                    (+r.SP_Paid || 0) * getRate("Supper", "Paid") +
                    (+r.SP_Reduced || 0) * getRate("Supper", "Reduced");
            }

            if (d >= prevStart && d <= prevEnd) {
                prev[key] = prev[key] || { Breakfast: 0, Lunch: 0, Snack: 0, Supper: 0 };
                prev[key].Breakfast +=
                    (+r.BR_Free || 0) * getRate("Breakfast", "Free") +
                    (+r.BR_Paid || 0) * getRate("Breakfast", "Paid") +
                    (+r.BR_Reduced || 0) * getRate("Breakfast", "Reduced");
                prev[key].Lunch +=
                    (+r.LN_Free || 0) * getRate("Lunch", "Free") +
                    (+r.LN_Paid || 0) * getRate("Lunch", "Paid") +
                    (+r.LN_Reduced || 0) * getRate("Lunch", "Reduced");
                prev[key].Snack += (+r.SN_Free || 0) * getRate("Snack", "Free");
                prev[key].Supper +=
                    (+r.SP_Free || 0) * getRate("Supper", "Free") +
                    (+r.SP_Paid || 0) * getRate("Supper", "Paid") +
                    (+r.SP_Reduced || 0) * getRate("Supper", "Reduced");
            }
        });

        return { reimbAggMonthly: curr, reimbAggMonthlyPrev: prev };
    }, [data, siteFilter, overOnly, isOver, syStart, syEnd, prevStart, prevEnd, getRate]);

    const chartDataDaily = useMemo(() => {
        if (!monthFilter) return null;
        const parsed = parseMonthFilter(monthFilter);
        if (!parsed) return null;

        const { monthIdx, year } = parsed;
        const daysInMonth = new Date(year, monthIdx + 1, 0).getDate();

        const base = Array.from({ length: daysInMonth }, (_, i) => {
            const day = i + 1;
            return {
                month: new Date(year, monthIdx, day).toLocaleDateString("en-US", { month: "short", day: "numeric" }),
                Breakfast: 0, Lunch: 0, Snack: 0, Supper: 0,
                BreakfastPrev: 0, LunchPrev: 0, SnackPrev: 0, SupperPrev: 0,
            };
        });

        data.forEach((r) => {
            const d = new Date(r.Date);
            if (isNaN(d)) return;
            if (d.getFullYear() !== year || d.getMonth() !== monthIdx) return;
            if (siteFilter && r.SchoolName !== siteFilter) return;
            if (overOnly && !isOver(r)) return;
            const i = d.getDate() - 1;
            sumInto(base[i], r);
        });

        const prevYear = year - 1;
        data.forEach((r) => {
            const d = new Date(r.Date);
            if (isNaN(d)) return;
            if (d.getFullYear() !== prevYear || d.getMonth() !== monthIdx) return;
            if (siteFilter && r.SchoolName !== siteFilter) return;
            if (overOnly && !isOver(r)) return;
            const i = d.getDate() - 1;
            const tmp = { Breakfast: 0, Lunch: 0, Snack: 0, Supper: 0 };
            sumInto(tmp, r);
            base[i].BreakfastPrev += tmp.Breakfast;
            base[i].LunchPrev += tmp.Lunch;
            base[i].SnackPrev += tmp.Snack;
            base[i].SupperPrev += tmp.Supper;
        });

        return base;
    }, [data, monthFilter, siteFilter, overOnly, isOver, parseMonthFilter]);

    const chartDataMonthly = useMemo(
        () =>
            monthOrderWithYears.map((monthYear) => {
                const [m, y] = monthYear.split(" ");
                const prevYear = parseInt(y, 10) - 1;
                const prevKey = `${m} ${prevYear}`;

                return {
                    month: m,
                    fullMonth: monthYear,
                    Breakfast: currentAggMonthly[monthYear]?.Breakfast || 0,
                    Lunch: currentAggMonthly[monthYear]?.Lunch || 0,
                    Snack: currentAggMonthly[monthYear]?.Snack || 0,
                    Supper: currentAggMonthly[monthYear]?.Supper || 0,
                    BreakfastPrev: prevAggMonthly[prevKey]?.Breakfast || 0,
                    LunchPrev: prevAggMonthly[prevKey]?.Lunch || 0,
                    SnackPrev: prevAggMonthly[prevKey]?.Snack || 0,
                    SupperPrev: prevAggMonthly[prevKey]?.Supper || 0,
                };
            }),
        [currentAggMonthly, prevAggMonthly, monthOrderWithYears]
    );

    const chartData = chartDataDaily || chartDataMonthly;

    const reimbChartDataMonthly = useMemo(
        () =>
            monthOrderWithYears.map((monthYear) => {
                const [m, y] = monthYear.split(" ");
                const prevYear = parseInt(y, 10) - 1;
                const prevKey = `${m} ${prevYear}`;

                return {
                    month: m,
                    fullMonth: monthYear,
                    Breakfast: reimbAggMonthly[monthYear]?.Breakfast || 0,
                    Lunch: reimbAggMonthly[monthYear]?.Lunch || 0,
                    Snack: reimbAggMonthly[monthYear]?.Snack || 0,
                    Supper: reimbAggMonthly[monthYear]?.Supper || 0,
                    BreakfastPrev: reimbAggMonthlyPrev[prevKey]?.Breakfast || 0,
                    LunchPrev: reimbAggMonthlyPrev[prevKey]?.Lunch || 0,
                    SnackPrev: reimbAggMonthlyPrev[prevKey]?.Snack || 0,
                    SupperPrev: reimbAggMonthlyPrev[prevKey]?.Supper || 0,
                };
            }),
        [reimbAggMonthly, reimbAggMonthlyPrev, monthOrderWithYears]
    );

    const handleChartClick = useCallback((dataPoint) => {
        if (dataPoint && dataPoint.activePayload && dataPoint.activePayload[0]) {
            const fullMonth = dataPoint.activePayload[0].payload.fullMonth;
            if (fullMonth) {
                setMonthFilter(fullMonth);
                setPage(0);
            }
        }
    }, []);

    const handleClearFilter = useCallback(() => {
        setMonthFilter("");
        setPage(0);
    }, []);

    const processRowUpdate = useCallback(async (newRow, oldRow) => {
        try {
            const { id, ...payload } = newRow;
            await fetch(`${API_MEAL_COUNTS}/${newRow.apiid}`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });
            setData((prev) => prev.map((r) => (r.apiid === newRow.apiid ? payload : r)));
            return newRow;
        } catch {
            return oldRow;
        }
    }, []);

    // ✅ memoize columns (DataGrid stops re-initting)
    const columns = useMemo(() => {
        return [
            {
                field: "Date",
                headerName: "Date",
                width: 120,
                valueFormatter: ({ value }) => {
                    const d = new Date(value);
                    return isNaN(d) ? value : d.toLocaleDateString("en-US");
                },
            },
            { field: "SchoolName", headerName: "Site", width: 180 },
            { field: "BR_Free", headerName: "BR Free", type: "number", width: 100, editable: true },
            { field: "BR_Paid", headerName: "BR Paid", type: "number", width: 100, editable: true },
            { field: "BR_Reduced", headerName: "BR Reduced", type: "number", width: 120, editable: true },
            { field: "LN_Free", headerName: "LN Free", type: "number", width: 100, editable: true },
            { field: "LN_Paid", headerName: "LN Paid", type: "number", width: 100, editable: true },
            { field: "LN_Reduced", headerName: "LN Reduced", type: "number", width: 120, editable: true },
            { field: "SN_Free", headerName: "SN Free", type: "number", width: 100, editable: true },
            { field: "SP_Free", headerName: "SP Free", type: "number", width: 100, editable: true },
            { field: "SP_Paid", headerName: "SP Paid", type: "number", width: 100, editable: true },
            { field: "SP_Reduced", headerName: "SP Reduced", type: "number", width: 120, editable: true },
            {
                field: "BR_participation",
                headerName: "BR %",
                type: "number",
                width: 80,
                valueGetter: (params) => getPart(params.row, "BR"),
                valueFormatter: ({ value }) => `${Number(value || 0).toFixed(1)}%`,
            },
            {
                field: "LN_participation",
                headerName: "LN %",
                type: "number",
                width: 80,
                valueGetter: (params) => getPart(params.row, "LN"),
                valueFormatter: ({ value }) => `${Number(value || 0).toFixed(1)}%`,
            },
            {
                field: "SN_participation",
                headerName: "SN %",
                type: "number",
                width: 80,
                valueGetter: (params) => getPart(params.row, "SN"),
                valueFormatter: ({ value }) => `${Number(value || 0).toFixed(1)}%`,
            },
            {
                field: "SP_participation",
                headerName: "SP %",
                type: "number",
                width: 80,
                valueGetter: (params) => getPart(params.row, "SP"),
                valueFormatter: ({ value }) => `${Number(value || 0).toFixed(1)}%`,
            },
            {
                field: "actions",
                headerName: "Actions",
                width: 100,
                sortable: false,
                filterable: false,
                renderCell: (params) => (
                    <IconButton
                        color="error"
                        onClick={async () => {
                            await fetch(`${API_MEAL_COUNTS}/${params.row.apiid}`, { method: "DELETE" });
                            setData((prev) => prev.filter((r) => r.apiid !== params.row.apiid));
                        }}
                    >
                        <DeleteIcon />
                    </IconButton>
                ),
            },
        ];
    }, [getPart]);

    const handleDownload = () => {
        if (!filtered.length) return;
        const headers = columns.filter((c) => c.field !== "actions").map((c) => c.headerName);
        const rowsCsv = filtered.map((row) =>
            columns
                .filter((c) => c.field !== "actions")
                .map((col) => {
                    const v = col.valueGetter ? col.valueGetter({ row }) : row[col.field] ?? "";
                    return typeof v === "string" && !/^\d+(\.\d+)?%?$/.test(v) ? `"${v}"` : v;
                })
                .join(",")
        );
        const csv = [headers.join(","), ...rowsCsv].join("\n");
        const blob = new Blob([csv], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "meal_counts_filtered.csv";
        a.click();
        URL.revokeObjectURL(url);
    };

    // ✅ memoize initialState (DataGrid stops resetting)
    const gridInitialState = useMemo(
        () => ({
            aggregation: {
                model: {
                    BR_Free: "sum",
                    BR_Paid: "sum",
                    BR_Reduced: "sum",
                    LN_Free: "sum",
                    LN_Paid: "sum",
                    LN_Reduced: "sum",
                    SN_Free: "sum",
                    SP_Free: "sum",
                    SP_Paid: "sum",
                    SP_Reduced: "sum",
                },
            },
        }),
        []
    );

    // ✅ stable pagination model + guarded updates (prevents loops)
    const paginationModel = useMemo(() => ({ page, pageSize }), [page, pageSize]);
    const handlePaginationModelChange = useCallback(
        (model) => {
            if (model.pageSize !== pageSize) {
                setPageSize(model.pageSize);
                setPage(0);
                return;
            }
            if (model.page !== page) setPage(model.page);
        },
        [page, pageSize]
    );

    return (
        <>
            <Modal open={loading} disableEscapeKeyDown>
                <Box
                    sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%,-50%)",
                        bgcolor: theme.palette.background.paper,
                        p: 4,
                        borderRadius: 2,
                        textAlign: "center",
                    }}
                >
                    <CircularProgress />
                    <Typography sx={{ mt: 2 }}>Loading meal counts…</Typography>
                </Box>
            </Modal>

            <Box m="20px">
                <Header title="Meal Counts Dashboard" subtitle="Jul 1 – Jun 30" />

                <Box display="flex" gap="20px" mb="20px" alignItems="center" flexWrap="wrap">
                    <FormControl sx={{ minWidth: 200 }}>
                        <InputLabel>School Year</InputLabel>
                        <Select
                            value={selectedSYStartYear}
                            onChange={(e) => {
                                setSelectedSYStartYear(e.target.value);
                                setMonthFilter("");
                            }}
                            label="School Year"
                        >
                            {schoolYears.map((opt) => (
                                <MenuItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl sx={{ minWidth: 200 }}>
                        <InputLabel>Site</InputLabel>
                        <Select value={siteFilter} onChange={(e) => setSiteFilter(e.target.value)} label="Site">
                            <MenuItem value="">All Sites</MenuItem>
                            {sites.map((s) => (
                                <MenuItem key={s} value={s}>
                                    {s}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl sx={{ minWidth: 200 }}>
                        <InputLabel>Month</InputLabel>
                        <Select value={monthFilter} onChange={(e) => setMonthFilter(e.target.value)} label="Month">
                            <MenuItem value="">All Months</MenuItem>
                            {months.map((m) => (
                                <MenuItem key={m} value={m}>
                                    {m}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {monthFilter && (
                        <Chip
                            icon={<FilterListIcon />}
                            label={`Filtered: ${monthFilter}`}
                            onDelete={handleClearFilter}
                            color="primary"
                            sx={{
                                fontWeight: 700,
                                fontSize: "0.9rem",
                                py: 2.5,
                            }}
                        />
                    )}

                    <FormControlLabel
                        control={<Switch checked={comparePrev} onChange={(e) => setComparePrev(e.target.checked)} />}
                        label="Compare Prev Year"
                    />

                    <FormControlLabel
                        control={<Switch checked={overOnly} onChange={(e) => setOverOnly(e.target.checked)} />}
                        label=">100% Only"
                    />

                    <Button
                        variant="contained"
                        sx={{ ml: "auto", backgroundColor: "#00AAFF", fontWeight: "bold" }}
                        onClick={handleDownload}
                    >
                        Download CSV
                    </Button>
                    <Button
                        variant="contained"
                        sx={{ backgroundColor: "#FF4500", fontWeight: "bold" }}
                        onClick={() => navigate("/mealcountsentry")}
                    >
                        Enter Meal Count
                    </Button>
                </Box>

                <Box display="grid" gridTemplateColumns="repeat(auto-fit, minmax(220px, 1fr))" gap={3} mb={4}>
                    <StatCard
                        title="Service Days"
                        value={statsData.serviceDays}
                        icon={<CalendarTodayIcon sx={{ fontSize: 48, color: "#00D4FF" }} />}
                        color="#00D4FF"
                        colors={colors}
                    />
                    <StatCard
                        title="Breakfast"
                        value={statsData.avgBreakfast}
                        icon={<FreeBreakfastIcon sx={{ fontSize: 48, color: "#FF4136" }} />}
                        color="#FF4136"
                        reimbursement={statsData.avgReimbBreakfast}
                        colors={colors}
                    />
                    <StatCard
                        title="Lunch"
                        value={statsData.avgLunch}
                        icon={<LunchDiningIcon sx={{ fontSize: 48, color: "#0074D9" }} />}
                        color="#0074D9"
                        reimbursement={statsData.avgReimbLunch}
                        colors={colors}
                    />
                    <StatCard
                        title="Snack"
                        value={statsData.avgSnack}
                        icon={<RestaurantIcon sx={{ fontSize: 48, color: "#2ECC40" }} />}
                        color="#2ECC40"
                        reimbursement={statsData.avgReimbSnack}
                        colors={colors}
                    />
                    <StatCard
                        title="Supper"
                        value={statsData.avgSupper}
                        icon={<LocalDiningIcon sx={{ fontSize: 48, color: "#FFDC00" }} />}
                        color="#FFDC00"
                        reimbursement={statsData.avgReimbSupper}
                        colors={colors}
                    />
                </Box>

                <ChartContainer
                    title="Meal Service Counts"
                    subtitle={monthFilter ? `Daily breakdown for ${monthFilter}` : "Total meals served per month"}
                    icon={<PeopleIcon sx={{ fontSize: 32, color: "#3498db" }} />}
                    accentColor="#3498db"
                    chartType="counts"
                    hoveredChart={hoveredChart}
                    setHoveredChart={setHoveredChart}
                    colors={colors}
                >
                    <ResponsiveContainer>
                        <AreaChart data={chartData}>
                            <defs>
                                <linearGradient id="gradBreakfast" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#FF4136" stopOpacity={0.8} />
                                    <stop offset="95%" stopColor="#FF4136" stopOpacity={0.1} />
                                </linearGradient>
                                <linearGradient id="gradLunch" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#0074D9" stopOpacity={0.8} />
                                    <stop offset="95%" stopColor="#0074D9" stopOpacity={0.1} />
                                </linearGradient>
                                <linearGradient id="gradSnack" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#2ECC40" stopOpacity={0.8} />
                                    <stop offset="95%" stopColor="#2ECC40" stopOpacity={0.1} />
                                </linearGradient>
                                <linearGradient id="gradSupper" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#FFDC00" stopOpacity={0.8} />
                                    <stop offset="95%" stopColor="#FFDC00" stopOpacity={0.1} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid stroke={colors.primary[300]} strokeDasharray="5 5" opacity={0.3} />
                            <XAxis dataKey="month" stroke={colors.grey[100]} style={{ fontSize: '0.875rem' }} />
                            <YAxis stroke={colors.grey[100]} style={{ fontSize: '0.875rem' }} />
                            <RechartTooltip
                                contentStyle={{
                                    backgroundColor: colors.primary[400],
                                    border: `1px solid ${colors.primary[300]}`,
                                    borderRadius: '8px',
                                    color: colors.grey[100]
                                }}
                            />
                            <Legend wrapperStyle={{ color: colors.grey[100] }} />

                            {comparePrev && (
                                <>
                                    <Area type="monotone" dataKey="BreakfastPrev" stroke="#FF4136" strokeWidth={2} dot={false} strokeDasharray="5 5" name="Breakfast (Prev)" fill="none" />
                                    <Area type="monotone" dataKey="LunchPrev" stroke="#0074D9" strokeWidth={2} dot={false} strokeDasharray="5 5" name="Lunch (Prev)" fill="none" />
                                    <Area type="monotone" dataKey="SnackPrev" stroke="#2ECC40" strokeWidth={2} dot={false} strokeDasharray="5 5" name="Snack (Prev)" fill="none" />
                                    <Area type="monotone" dataKey="SupperPrev" stroke="#FFDC00" strokeWidth={2} dot={false} strokeDasharray="5 5" name="Supper (Prev)" fill="none" />
                                </>
                            )}

                            <Area type="monotone" dataKey="Breakfast" stroke="#FF4136" strokeWidth={3} fill="url(#gradBreakfast)" name="Breakfast" />
                            <Area type="monotone" dataKey="Lunch" stroke="#0074D9" strokeWidth={3} fill="url(#gradLunch)" name="Lunch" />
                            <Area type="monotone" dataKey="Snack" stroke="#2ECC40" strokeWidth={3} fill="url(#gradSnack)" name="Snack" />
                            <Area type="monotone" dataKey="Supper" stroke="#FFDC00" strokeWidth={3} fill="url(#gradSupper)" name="Supper" />
                        </AreaChart>
                    </ResponsiveContainer>
                </ChartContainer>

                <ChartContainer
                    title="Reimbursement Income"
                    subtitle="Total reimbursement amounts by meal service"
                    icon={<AccountBalanceWalletIcon sx={{ fontSize: 32, color: "#27ae60" }} />}
                    accentColor="#27ae60"
                    chartType="income"
                    hoveredChart={hoveredChart}
                    setHoveredChart={setHoveredChart}
                    colors={colors}
                >
                    <ResponsiveContainer>
                        <BarChart data={reimbChartDataMonthly} barGap={0} onClick={handleChartClick}>
                            <defs>
                                <linearGradient id="barGradBreakfast" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor="#FF4136" stopOpacity={1} />
                                    <stop offset="100%" stopColor="#FF413660" stopOpacity={0.6} />
                                </linearGradient>
                                <linearGradient id="barGradLunch" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor="#0074D9" stopOpacity={1} />
                                    <stop offset="100%" stopColor="#0074D960" stopOpacity={0.6} />
                                </linearGradient>
                                <linearGradient id="barGradSnack" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor="#2ECC40" stopOpacity={1} />
                                    <stop offset="100%" stopColor="#2ECC4060" stopOpacity={0.6} />
                                </linearGradient>
                                <linearGradient id="barGradSupper" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor="#FFDC00" stopOpacity={1} />
                                    <stop offset="100%" stopColor="#FFDC0060" stopOpacity={0.6} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid stroke={colors.primary[300]} strokeDasharray="5 5" opacity={0.3} />
                            <XAxis dataKey="month" stroke={colors.grey[100]} style={{ cursor: "pointer", fontSize: '0.875rem' }} />
                            <YAxis stroke={colors.grey[100]} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}K`} style={{ fontSize: '0.875rem' }} />
                            <RechartTooltip
                                contentStyle={{
                                    backgroundColor: colors.primary[400],
                                    border: `1px solid ${colors.primary[300]}`,
                                    borderRadius: '8px',
                                    color: colors.grey[100]
                                }}
                                formatter={(val) => [
                                    `$${Number(val).toLocaleString("en-US", {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                    })}`,
                                ]}
                                cursor={{ fill: "rgba(255, 255, 255, 0.05)" }}
                            />
                            <Legend wrapperStyle={{ color: colors.grey[100] }} />

                            <Bar dataKey="Breakfast" fill="url(#barGradBreakfast)" name="Breakfast $" radius={[8, 8, 0, 0]} style={{ cursor: "pointer" }} />
                            <Bar dataKey="Lunch" fill="url(#barGradLunch)" name="Lunch $" radius={[8, 8, 0, 0]} style={{ cursor: "pointer" }} />
                            <Bar dataKey="Snack" fill="url(#barGradSnack)" name="Snack $" radius={[8, 8, 0, 0]} style={{ cursor: "pointer" }} />
                            <Bar dataKey="Supper" fill="url(#barGradSupper)" name="Supper $" radius={[8, 8, 0, 0]} style={{ cursor: "pointer" }} />
                        </BarChart>
                    </ResponsiveContainer>
                </ChartContainer>

                <Box
                    height={600}
                    sx={{
                        "& .MuiDataGrid-root": { border: "none", color: theme.palette.text.primary },
                        "& .MuiDataGrid-cell": { color: theme.palette.text.primary },
                        "& .MuiDataGrid-columnHeaders": { backgroundColor: colors.primary[500], color: theme.palette.text.primary },
                        "& .MuiDataGrid-columnHeaderTitle": { fontWeight: "bold" },
                        "& .MuiDataGrid-row:nth-of-type(even)": { backgroundColor: theme.palette.action.hover },
                        "& .MuiDataGrid-row:nth-of-type(odd)": { backgroundColor: theme.palette.background.paper },
                        "& .MuiDataGrid-row.over-part": { backgroundColor: "rgba(255, 0, 0, 0.12) !important" },
                    }}
                >
                    <DataGridPro
                        getRowId={(row) => row.apiid}
                        rows={filtered}
                        columns={columns}
                        pagination
                        paginationModel={paginationModel}
                        onPaginationModelChange={handlePaginationModelChange}
                        pageSizeOptions={[10, 20, 50, 100]}
                        disableSelectionOnClick
                        editMode="cell"
                        processRowUpdate={processRowUpdate}
                        onProcessRowUpdateError={(err) => console.error(err)}
                        getRowClassName={(params) => {
                            const over =
                                getPart(params.row, "BR") > 100 ||
                                getPart(params.row, "LN") > 100 ||
                                getPart(params.row, "SN") > 100 ||
                                getPart(params.row, "SP") > 100;
                            return over ? "over-part" : "";
                        }}
                        initialState={gridInitialState}
                    />
                </Box>

                <Modal open={showForm} onClose={() => setShowForm(false)}>
                    <Box
                        sx={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%,-50%)",
                            width: "80%",
                            height: "80%",
                            bgcolor: theme.palette.background.paper,
                            borderRadius: 2,
                            boxShadow: 24,
                        }}
                    >
                        <Button
                            variant="contained"
                            onClick={() => setShowForm(false)}
                            sx={{ position: "absolute", top: 8, right: 8, zIndex: 10 }}
                        >
                            Close
                        </Button>
                        <Box
                            component="iframe"
                            src="https://liquidoffice.figk-12.com:8443/lfserver/ss_FINANCE_MealCounts"
                            width="100%"
                            height="100%"
                            sx={{ border: "none", borderRadius: 1 }}
                        />
                    </Box>
                </Modal>
            </Box>
        </>
    );
}